import 'leaflet/dist/leaflet.css';
import './styles.css';
import { narrationVerdict, simulateDrive, type FeatureFixture, type SimFeature, type SimResult, type TrackPoint, type WarmedLandmark } from '@roadtrip/core';
import { checkHealth, loadSettings, saveSettings, warm } from './api';
import { isFeature, withFeatures, type Story } from './features';
import { RouteMap } from './map';
import { Playback } from './playback';
import { PRESETS, geocode, osrmShape, trackFromGpxFile, trackFromShape, type LatLon } from './route';
import { downloadSnapshot, fetchSnapshot, readSnapshotFile, type Snapshot } from './snapshot';
import { Speech } from './speech';

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;
const esc = (s: unknown): string => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const mmss = (s: number): string => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const km = (m: number): string => (m / 1000).toFixed(2);

// ------------------------------------------------------------------ state

interface State {
  name: string;
  source: 'routed' | 'gpx';
  track: TrackPoint[] | null;
  landmarks: WarmedLandmark[] | null;
  /** Rivers, forests and wilderness the warm's corridor calls sent (with each call's shapes). */
  features: FeatureFixture | null;
  /** Features are folded into result.narrations in drive order (features.ts); a card or section index covers both. */
  result: SimResult | null;
  selected: number | null;
}
const state: State = { name: '', source: 'routed', track: null, landmarks: null, features: null, result: null, selected: null };
const settings = loadSettings();

const statusEl = $('status');
const speedSel = $<HTMLSelectElement>('speed');
const warmBtn = $<HTMLButtonElement>('warmBtn');
const runBtn = $<HTMLButtonElement>('runBtn');
const exportBtn = $<HTMLButtonElement>('exportBtn');
const drawer = $('drawer');
// Phone layout: the route form hides behind a toggle and Settings becomes a bottom sheet.
const narrow = window.matchMedia('(max-width: 640px)');

const speech = new Speech($<HTMLSelectElement>('voice'), settings.voice);
const map = new RouteMap($('map'), (i) => select(i, true));
const playback = new Playback(map, speech, () => Number($<HTMLSelectElement>('rate').value), () => $<HTMLInputElement>('chime').checked, (i, note) => {
  document.querySelectorAll('.card').forEach((c, k) => c.classList.toggle('playing', k === i));
  $('now').textContent = note;
}, $('arrow'));

function status(text: string, opts: { error?: boolean; progress?: number } = {}): void {
  statusEl.classList.toggle('err', !!opts.error);
  statusEl.innerHTML = opts.progress != null ? `<span class="bar-fill"><i style="width:${Math.round(opts.progress * 100)}%"></i></span>${esc(text)}` : esc(text);
}

const speedMps = (): number => Number(speedSel.value);
const mph = (mps: number): number => Math.round(mps * 2.237);

// ------------------------------------------------------------------ route input

async function setRoute(
  name: string,
  shape: LatLon[] | null,
  track: TrackPoint[] | null,
  source: State['source'],
  landmarks: WarmedLandmark[] | null,
  features: FeatureFixture | null = null,
): Promise<void> {
  playback.stop();
  state.name = name;
  state.source = source;
  state.track = track ?? trackFromShape(shape!, speedMps());
  state.landmarks = landmarks;
  state.features = features;
  state.result = null;
  state.selected = null;
  map.setTrack(state.track);
  renderResult();
  warmBtn.disabled = false;
  runBtn.disabled = !landmarks;
  exportBtn.disabled = !landmarks;
  const mins = Math.round(state.track[state.track.length - 1]!.t / 60000);
  status(`${name}: ${state.track.length} fixes, about ${mins} min of driving${landmarks ? `, ${landmarks.length} landmarks loaded` : ''}.`);
  if (narrow.matches) showRouteForm(false);
  if (landmarks) await run();
}

async function routeBetween(name: string, waypoints: LatLon[]): Promise<void> {
  status(`Routing ${name}…`);
  const { shape, distanceM } = await osrmShape(waypoints);
  await setRoute(`${name} (${(distanceM / 1609.344).toFixed(0)} mi)`, shape, null, 'routed', null);
}

async function loadPreset(i: number): Promise<void> {
  const p = PRESETS[i];
  if (!p) return;
  try {
    if (p.snapshot) {
      const snap = await fetchSnapshot(p.snapshot);
      if (snap) {
        await loadSnapshot(snap);
        return;
      }
    }
    await routeBetween(p.name, p.waypoints);
  } catch (e) {
    status((e as Error).message, { error: true });
  }
}

async function loadSnapshot(s: Snapshot): Promise<void> {
  const track = s.source === 'routed' ? trackFromShape(s.track, speedMps()) : s.track;
  await setRoute(s.name, null, track, s.source, s.landmarks, s.features ?? null);
}

$<HTMLFormElement>('routeForm').addEventListener('submit', async (e) => {
  e.preventDefault();
  const from = $<HTMLInputElement>('from').value.trim();
  const to = $<HTMLInputElement>('to').value.trim();
  if (!from || !to) {
    status('Type a start and an end, or pick them on the map.', { error: true });
    return;
  }
  try {
    status(`Looking up ${from} and ${to}…`);
    const a = await geocode(from);
    // Nominatim asks for at most one request a second.
    await new Promise((r) => setTimeout(r, 1100));
    const b = await geocode(to);
    await routeBetween(`${a.label} → ${b.label}`, [a, b]);
  } catch (err) {
    status((err as Error).message, { error: true });
  }
});

function showRouteForm(open: boolean): void {
  document.querySelector('.app')!.classList.toggle('route-open', open);
  $('routeToggle').setAttribute('aria-expanded', String(open));
}
$('routeToggle').addEventListener('click', () => showRouteForm(!document.querySelector('.app')!.classList.contains('route-open')));

let stopPicking: (() => void) | null = null;
$('pickBtn').addEventListener('click', () => {
  if (stopPicking) {
    stopPicking();
    stopPicking = null;
    status('Pick cancelled.');
    return;
  }
  status('Click the map where the drive starts.');
  stopPicking = map.pick(
    async (a, b) => {
      stopPicking = null;
      try {
        await routeBetween(`${a.lat.toFixed(3)},${a.lon.toFixed(3)} → ${b.lat.toFixed(3)},${b.lon.toFixed(3)}`, [a, b]);
      } catch (e) {
        status((e as Error).message, { error: true });
      }
    },
    () => status('Now click where it ends.'),
  );
});

$<HTMLInputElement>('gpxFile').addEventListener('change', async (e) => {
  const f = (e.target as HTMLInputElement).files?.[0];
  if (!f) return;
  try {
    await setRoute(f.name, null, await trackFromGpxFile(f), 'gpx', null);
  } catch (err) {
    status((err as Error).message, { error: true });
  }
  (e.target as HTMLInputElement).value = '';
});

speedSel.addEventListener('change', async () => {
  if (!state.track || state.source !== 'routed') return;
  state.track = trackFromShape(state.track, speedMps());
  map.setTrack(state.track);
  if (state.landmarks) await run();
});

// ------------------------------------------------------------------ warm + run

let warmAbort: AbortController | null = null;
warmBtn.addEventListener('click', async () => {
  if (warmAbort) {
    warmAbort.abort();
    return;
  }
  if (!state.track) return;
  const health = await checkHealth(settings.baseUrl);
  if (!health.ok) {
    status(`Worker ${health.detail}. Check the URL in Settings.`, { error: true });
    return;
  }
  warmAbort = new AbortController();
  warmBtn.textContent = 'Stop warming';
  playback.stop();
  try {
    const { landmarks, features } = await warm(
      state.track,
      settings,
      (p) => {
        const frac = Math.min(0.95, ((p.pass - 1) * p.steps + p.step) / (p.steps * 4));
        status(`Pass ${p.pass} · point ${p.step}/${p.steps} · ${p.ready} ready · ${p.pending} cooking${p.warming ? '' : ' · Worker idle'}`, { progress: frac });
      },
      warmAbort.signal,
    );
    state.landmarks = landmarks;
    state.features = features ?? null;
    runBtn.disabled = exportBtn.disabled = landmarks.length === 0;
    status(`${landmarks.length} landmarks${features ? ` and ${features.features.length} rivers or forests` : ''} along the route (${health.detail}).`);
    await run();
  } catch (e) {
    const err = e as Error & { code?: string | null };
    if (err.name === 'AbortError') status('Warming stopped.');
    else if (err.code === 'demo_token_required') status('The Worker wants the demo token. Open Settings and paste it.', { error: true });
    else status(`Warming failed: ${err.message}`, { error: true });
  } finally {
    warmAbort = null;
    warmBtn.textContent = 'Warm & run';
  }
});

runBtn.addEventListener('click', () => void run());

async function run(): Promise<void> {
  if (!state.track || !state.landmarks) return;
  playback.stop();
  const t0 = performance.now();
  const raw = await simulateDrive(state.track, state.landmarks, { features: state.features ?? undefined });
  state.result = withFeatures(raw);
  state.selected = null;
  map.showResult(state.result);
  renderResult();
  const f = raw.features.length;
  status(
    `${raw.narrations.length} narrations${f ? `, ${f} rivers or forests` : ''}, ${raw.dropped.length} skipped, at ${mph(speedMps())} mph. Simulated in ${Math.round(performance.now() - t0)} ms.`,
  );
}

// ------------------------------------------------------------------ render

function renderResult(): void {
  const r = state.result;
  const stats = $('stats');
  const cards = $('cards');
  const dropped = $('dropped');
  $('player').hidden = !r || r.narrations.length === 0;
  if (!r) {
    stats.innerHTML = '';
    cards.innerHTML = state.track
      ? `<p class="empty">${state.landmarks ? 'Running…' : 'Press Warm & run to ask the Worker for landmarks along this road and simulate the drive.'}</p>`
      : '';
    dropped.innerHTML = '';
    return;
  }
  const good = r.narrations.filter((n) => !isFeature(n) && narrationVerdict(n, r.closest.get(n.pageId)).tone === 'good').length;
  const okish = r.narrations.filter((n) => !isFeature(n) && narrationVerdict(n, r.closest.get(n.pageId)).tone === 'ok').length;
  stats.innerHTML = [
    `Drive <b>${mmss(r.durationSec)}</b>`,
    `Narrated <b>${r.narrations.filter((n) => !isFeature(n)).length}</b>`,
    ...(r.features.length ? [`Rivers and forests <b>${r.features.length}</b>`] : []),
    `Finished before passing <b>${good}</b>`,
    `Ran slightly past <b>${okish}</b>`,
    `Skipped <b>${r.dropped.length}</b>`,
  ]
    .map((s) => `<span class="stat">${s}</span>`)
    .join('');

  cards.innerHTML = r.narrations.length
    ? r.narrations
        .map((n, i) => {
          if (isFeature(n)) return featureCard(n, i);
          const v = narrationVerdict(n, r.closest.get(n.pageId));
          const c = r.closest.get(n.pageId);
          const lm = n.landmark as WarmedLandmark;
          return `<article class="card" data-i="${i}" id="card-${i}">
  <header>
    <span class="idx s${i % 6}">${i + 1}</span>
    <div><h3>${esc(lm.title)}</h3><p class="headline">${esc(lm.headline ?? '')}</p></div>
    <span class="verdict ${v.tone}">${esc(v.label)}</span>
  </header>
  <div class="timeline">
    <div><span class="k">Triggered</span><span class="v">${mmss(n.triggeredAt)}</span><span class="d">${km(n.distAtTrigger)} km out</span></div>
    <div><span class="k">Voice began</span><span class="v">${mmss(n.startedAt)}</span><span class="d">${km(n.distAtStart)} km out</span></div>
    <div><span class="k">Closest</span><span class="v">${c ? mmss(c.t) : '—'}</span><span class="d">${c ? km(c.d) + ' km away' : ''}</span></div>
    <div><span class="k">Voice ended</span><span class="v">${n.endedAt != null ? mmss(n.endedAt) : '—'}</span><span class="d">${n.distAtEnd != null ? km(n.distAtEnd) + ' km out' : '—'}</span></div>
  </div>
  <p class="detail">${esc(v.detail)}</p>
  <div class="actions">
    <button type="button" class="btn" data-play="${i}">Play this section</button>
    <span class="hint">${esc(n.intro)}</span>
  </div>
  <details>
    <summary>Read the script (about ${lm.est_seconds} s${lm.tone ? ', ' + esc(lm.tone) : ''})</summary>
    <blockquote><em class="intro">${esc(n.intro)}</em> ${esc(lm.script)}</blockquote>
    ${lm.wiki_url ? `<p class="src">Written from <a href="${esc(lm.wiki_url)}" target="_blank" rel="noopener">${esc(lm.title)} on Wikipedia</a>, CC BY-SA 4.0.</p>` : ''}
  </details>
</article>`;
        })
        .join('')
    : '<p class="empty">Nothing triggered on this drive. Either no landmark sits close enough to the road, or the Worker has none here yet.</p>';

  dropped.innerHTML = r.dropped.length
    ? `<h2>Not narrated</h2><table>${r.dropped
        .map(
          (d) =>
            `<tr><td class="t">${mmss(d.at)}</td><td>${esc(d.landmark.title)}</td><td>${esc(
              d.reason === 'passed' ? 'already driven past' : d.reason === 'displaced' ? 'queue full, a nearer one won' : 'drove out of range',
            )}</td><td class="t">${km(d.dist)} km</td></tr>`,
        )
        .join('')}</table>`
    : '';

  cards.querySelectorAll<HTMLElement>('.card').forEach((card) => {
    card.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).closest('button, a, summary')) return;
      select(Number(card.dataset.i), false);
    });
  });
  cards.querySelectorAll<HTMLButtonElement>('[data-play]').forEach((b) => {
    b.addEventListener('click', () => {
      const i = Number(b.dataset.play);
      if (playback.current === i) playback.stop();
      else if (state.result) playback.play(state.result, i);
    });
  });
}

const FEATURE_WHY: Record<string, string> = { river: 'beside it', forest: 'through it', wilderness: 'along it' };

/** A river, forest or wilderness: when it became eligible and where the voice began, no verdict. */
function featureCard(n: Story & { feature: SimFeature }, i: number): string {
  const f = n.feature;
  const ft = f.feature;
  return `<article class="card feature" data-i="${i}" id="card-${i}">
  <header>
    <span class="idx s${i % 6}">${i + 1}</span>
    <div><h3>${esc(ft.title)}</h3><p class="headline">${esc(ft.headline ?? '')}</p></div>
    <span class="verdict ok">${esc(ft.kind)}</span>
  </header>
  <div class="timeline">
    <div><span class="k">Eligible</span><span class="v">${mmss(f.queuedAt)}</span><span class="d">after ${(f.runM / 1000).toFixed(1)} km ${FEATURE_WHY[ft.kind] ?? ''}</span></div>
    <div><span class="k">Voice began</span><span class="v">${mmss(f.startedAt)}</span><span class="d">in the first quiet gap</span></div>
    <div><span class="k">Voice ended</span><span class="v">${f.endedAt != null ? mmss(f.endedAt) : '—'}</span><span class="d">${esc(f.reason ?? '')}</span></div>
  </div>
  <div class="actions">
    <button type="button" class="btn" data-play="${i}">Play this section</button>
    <span class="hint">${esc(n.intro)}${f.placeholder ? ' (placeholder script)' : ''}</span>
  </div>
  <details>
    <summary>Read the story (about ${ft.est_seconds} s)</summary>
    <blockquote><em class="intro">${esc(n.intro)}</em> ${esc(ft.script)}</blockquote>
    ${ft.wiki_url ? `<p class="src">Written from <a href="${esc(ft.wiki_url)}" target="_blank" rel="noopener">${esc(ft.title)} on Wikipedia</a> (CC BY-SA 4.0) with facts from Wikidata (CC0).</p>` : ''}
  </details>
</article>`;
}

function select(i: number, scroll: boolean): void {
  if (!state.result) return;
  state.selected = i;
  document.querySelectorAll('.card').forEach((c, k) => c.classList.toggle('selected', k === i));
  const n = state.result.narrations[i];
  if (n) map.fitSection(state.result, n);
  map.highlight(i);
  if (scroll) document.getElementById(`card-${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
}

$('playAll').addEventListener('click', () => state.result && playback.playAll(state.result));
$('stopBtn').addEventListener('click', () => playback.stop());

// ------------------------------------------------------------------ settings

const baseUrlIn = $<HTMLInputElement>('baseUrl');
const tokenIn = $<HTMLInputElement>('token');
baseUrlIn.value = settings.baseUrl;
tokenIn.value = settings.token;
function showDrawer(open: boolean): void {
  drawer.hidden = !open;
  $('settingsBtn').setAttribute('aria-expanded', String(open));
  // Sit just under the toolbar, which wraps to different heights at different widths. On a phone
  // the drawer is a fixed bottom sheet instead, so any earlier offset must go.
  drawer.style.top = open && !narrow.matches ? `${document.querySelector('.bar')!.getBoundingClientRect().bottom + 8}px` : '';
}
$('settingsBtn').addEventListener('click', () => showDrawer(drawer.hidden));
$('closeDrawer').addEventListener('click', () => showDrawer(false));
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && !drawer.hidden) showDrawer(false);
});
document.addEventListener('click', (e) => {
  if (drawer.hidden) return;
  const t = e.target as HTMLElement;
  if (!drawer.contains(t) && !t.closest('#settingsBtn')) showDrawer(false);
});
$<HTMLInputElement>('chime').checked = settings.chime;
$<HTMLInputElement>('chime').addEventListener('change', (e) => {
  settings.chime = (e.target as HTMLInputElement).checked;
  saveSettings(settings);
});
for (const el of [baseUrlIn, tokenIn]) {
  el.addEventListener('change', () => {
    settings.baseUrl = baseUrlIn.value.trim().replace(/\/$/, '');
    settings.token = tokenIn.value.trim();
    saveSettings(settings);
  });
}
$<HTMLSelectElement>('voice').addEventListener('change', () => {
  settings.voice = speech.voiceName;
  saveSettings(settings);
});
$<HTMLSelectElement>('rate').value = String(settings.rate);
$<HTMLSelectElement>('rate').addEventListener('change', (e) => {
  settings.rate = Number((e.target as HTMLSelectElement).value);
  saveSettings(settings);
});
exportBtn.addEventListener('click', () => {
  if (!state.track || !state.landmarks) return;
  downloadSnapshot({ name: state.name, source: state.source, speedMps: speedMps(), track: state.track, landmarks: state.landmarks, ...(state.features ? { features: state.features } : {}) });
});
$<HTMLInputElement>('snapshotFile').addEventListener('change', async (e) => {
  const f = (e.target as HTMLInputElement).files?.[0];
  if (!f) return;
  try {
    await loadSnapshot(await readSnapshotFile(f));
  } catch (err) {
    status((err as Error).message, { error: true });
  }
  (e.target as HTMLInputElement).value = '';
});

// ------------------------------------------------------------------ boot

const presetSel = $<HTMLSelectElement>('preset');
presetSel.innerHTML = PRESETS.map((p, i) => `<option value="${i}">${esc(p.name)}</option>`).join('');
presetSel.addEventListener('change', () => void loadPreset(Number(presetSel.value)));
if (narrow.matches) document.querySelector<HTMLDetailsElement>('.legend')!.open = false;
if (!speech.available) status('This browser has no speech engine; sections will play silently.', { error: true });
void loadPreset(0);

// Handy from the console while poking at a run: routeLab.state, routeLab.map.map (the Leaflet map).
(window as unknown as { routeLab: unknown }).routeLab = { state, map, playback, settings };
